import { loadScript } from './loadScript';
import { WalletCancelledError } from './walletErrors';
import type { GooglePayPaymentAction, Location, PaymentMethod } from '@/api/types';

/**
 * Google Pay on the web.
 *
 * Two jobs that look similar and are not. The PROBE asks whether this browser and this guest's Google account
 * could pay at all, and has to answer before the method screen renders — long before a payment exists. The
 * SHEET runs a real payment from a request the server assembled. Only the probe needs anything built here;
 * the sheet's request is passed through untouched.
 */

const SDK_URL = 'https://pay.google.com/gp/p/js/pay.js';

/**
 * Live money only in production. Anywhere else Google's TEST environment returns fake, unchargeable cards.
 *
 * FROM `MODE`, NOT `PROD`, and the difference is the whole point. Vite's `PROD` tracks NODE_ENV, and
 * `vite build` sets NODE_ENV=production whatever `--mode` it is given - so `PROD` is true in the dev build
 * too. This read `PROD` and therefore ran Google Pay in PRODUCTION everywhere, against dev's placeholder
 * merchant id, which Google rejects in its own sheet as OR_BIBED_11 before anything reaches our servers or
 * the gateway.
 *
 * `MODE` is the flag the pipeline actually varies: `npm run build:dev` for dev, `npm run build` for
 * production. Exported and parameterised so the mapping is testable - the bug was invisible in a unit test
 * precisely because it only existed in a built bundle.
 */
export function googlePayEnvironment(mode: string = import.meta.env.MODE): 'TEST' | 'PRODUCTION' {
    return mode === 'production' ? 'PRODUCTION' : 'TEST';
}

const ENVIRONMENT = googlePayEnvironment();

/** How long to wait for an answer before deciding there is not one. See the race in `isGooglePayAvailable`. */
const PROBE_TIMEOUT_MS = 2_500;

interface GooglePaymentsClient {
    isReadyToPay(request: unknown): Promise<{ result: boolean }>;
    loadPaymentData(request: unknown): Promise<Record<string, unknown>>;
}

declare global {
    interface Window {
        google?: {
            payments?: {
                api?: {
                    PaymentsClient: new (options: { environment: string }) => GooglePaymentsClient;
                };
            };
        };
    }
}

/**
 * The card networks this location takes, in Google's spelling.
 *
 * Derived from `paymentMethodsAccepted` rather than invented, so the probe asks about the same cards the
 * server will actually accept. Anything Google has no name for is dropped rather than guessed — a gift card
 * is not a network, and a wrong entry makes `isReadyToPay` throw rather than answer.
 */
const NETWORKS: Partial<Record<PaymentMethod, string>> = {
    visa: 'VISA',
    mastercard: 'MASTERCARD',
    amex: 'AMEX',
    discover: 'DISCOVER',
    jcb: 'JCB',
};

export function allowedCardNetworks(location: Location): string[] {
    return location.paymentMethodsAccepted.map((method) => NETWORKS[method]).filter((n): n is string => !!n);
}

/**
 * The smallest request that answers "could this guest pay with Google Pay".
 *
 * Deliberately not the real `paymentDataRequest`: that one carries the amount and the gateway's merchant
 * credentials and is assembled server-side, and it does not exist yet at the point this question is asked.
 * A probe needs only the API version and which cards are welcome.
 */
export function readinessRequest(location: Location) {
    return {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [
            {
                type: 'CARD',
                parameters: {
                    allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                    allowedCardNetworks: allowedCardNetworks(location),
                },
            },
        ],
    };
}

async function client(): Promise<GooglePaymentsClient> {
    await loadScript(SDK_URL);

    const PaymentsClient = window.google?.payments?.api?.PaymentsClient;
    if (!PaymentsClient) throw new Error('Google Pay SDK loaded but exposed no PaymentsClient.');

    return new PaymentsClient({ environment: ENVIRONMENT });
}

/**
 * Whether this browser can present a Google Pay sheet with something in it.
 *
 * Replaces the placeholder that trusted the location's configuration and assumed the device could manage it.
 * Google Pay on the web is not Android-only, so there was never an honest environment test — this is the
 * question Google documents, and it is the only thing that knows whether the guest has a usable card.
 *
 * Any failure is a no. A guest is better off not seeing the button than seeing one that opens an empty sheet,
 * and a location with no recognised networks cannot be asked about at all.
 */
export async function isGooglePayAvailable(location: Location): Promise<boolean> {
    if (allowedCardNetworks(location).length === 0) return false;

    const probe = (async () => {
        const payments = await client();
        const { result } = await payments.isReadyToPay(readinessRequest(location));

        return result === true;
    })();

    /*
     * A deadline, because this answer sits on the path a guest takes to pay.
     *
     * `pay.google.com` is a common entry in blocklists, and a blocked script tag neither loads nor errors —
     * it simply never resolves. Without a race the tip step would await a promise that settles never, and a
     * guest would tap Pay and watch nothing happen. Two and a half seconds is far longer than the SDK takes
     * on a working connection and short enough not to read as a hang.
     */
    const deadline = new Promise<boolean>((resolve) => {
        setTimeout(() => resolve(false), PROBE_TIMEOUT_MS);
    });

    try {
        return await Promise.race([probe, deadline]);
    } catch {
        return false;
    }
}

/**
 * Opens the sheet and returns whatever the guest authorised.
 *
 * The request is the server's, passed through unread — it carries the amount and the gateway's own merchant
 * parameters, and this app has no business editing either. What comes back is equally opaque and goes
 * straight to `/payments/{id}/complete`.
 *
 * A guest closing the sheet rejects, which the caller treats as "they changed their mind", not as a failure
 * worth showing an error for.
 */
export async function openGooglePaySheet(
    action: GooglePayPaymentAction,
): Promise<Record<string, unknown>> {
    const payments = await client();

    try {
        return await payments.loadPaymentData(action.paymentDataRequest);
    } catch (error) {
        // Google reports a dismissal with a status code; Apple fires an `oncancel` handler and never
        // rejects. Both become one type here so the caller has a single thing to check.
        if ((error as { statusCode?: string } | null)?.statusCode === 'CANCELED') {
            throw new WalletCancelledError();
        }

        throw error;
    }
}
